import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Cpu, Activity, Play } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import AiRoleTestModal from './AiRoleTestModal';
import PromptEditorModal from './PromptEditorModal';
import { FileText } from 'lucide-react';

const ModelSwitcher = () => {
    const { toast } = useToast();
    const [configs, setConfigs] = useState([]); // from /api/admin/ai/models
    const [availableModels, setAvailableModels] = useState([]); // from /api/models (LLM Switch discovery)
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Test Modal State
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [testRole, setTestRole] = useState(null);

    // Prompt Edit Modal State
    const [promptModalOpen, setPromptModalOpen] = useState(false);
    const [promptEditRole, setPromptEditRole] = useState(null); // { key, label, model }

    const openTestModal = (roleKey, roleLabel) => {
        setTestRole({ key: roleKey, label: roleLabel });
        setTestModalOpen(true);
    };

    const openPromptModal = (roleKey, roleLabel, modelName) => {
        setPromptEditRole({ key: roleKey, label: roleLabel, model: modelName });
        setPromptModalOpen(true);
    };

    // Default configuration to ensure no NULL values are sent to DB
    const DEFAULT_CONFIG = {
        model_name: 'mistral-large:123b',
        temperature: 0.7,
        max_tokens: 2048,
        is_active: true,
        description: ''
    };

    // Hardcoded roles we expect the system to use
    // We could also dynamically discover them from DB configs
    const standardRoles = [
        { key: 'default_chat', label: 'Default Chat', desc: 'General conversational assistant.' },
        { key: 'rag_answer', label: 'RAG Answer (Knowledge Base)', desc: 'Generates answers based on PIM data.' },
        { key: 'document_analysis', label: 'Document Analysis (Extraction)', desc: 'Analyzes uploaded documents to extract properties.' },
        { key: 'rag_embedding', label: 'Embedding Model', desc: 'Generates vectors for search. CAUTION: Changing requires re-indexing.' },
        { key: 'seo_description', label: 'Product Description (SEO)', desc: 'Generates marketing text for products.' },
        { key: 'voice_answer', label: 'Voice Assistant', desc: 'Short, concise responses for voice.' },
        { key: 'sql_intent_model', label: 'SQL Intent Detection', desc: 'Detects user intent for database queries.' },
        { key: 'voice_synthesis', label: 'Text-to-Speech (TTS)', desc: 'Converts text responses into spoken audio using Qwen 3 TTS.' },
        { key: 'voice_transcription', label: 'Audio Transcription', desc: 'Converts user voice into text using Whisper Large-v3.' }
    ];

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const [modelRes, configRes] = await Promise.all([
                fetch('/api/models'), // Discovery
                fetch('/api/admin/ai/models') // Specific Configs
            ]);

            if (modelRes.ok) {
                const data = await modelRes.json();
                setAvailableModels(data || []);
            }
            if (configRes.ok) {
                const data = await configRes.json();
                setConfigs(data || []);
            }
        } catch (err) {
            console.error("Failed to fetch data", err);
            toast.error("Failed to load AI configurations");
        }
        setLoading(false);
    }, [toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getConfigForRole = (roleKey) => {
        return configs.find(c => c.role_name === roleKey) || {
            ...DEFAULT_CONFIG,
            role_name: roleKey
        };
    };

    const handleConfigChange = (roleKey, field, value) => {
        setConfigs(prev => {
            const idx = prev.findIndex(c => c.role_name === roleKey);
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = { ...updated[idx], [field]: value };
                return updated;
            } else {
                // Add new mock config entry until saved, ensuring defaults fall in place is handled by getConfigForRole visually,
                // BUT we need to ensure the state has the full object for when we click save/update immediately after
                return [...prev, {
                    ...DEFAULT_CONFIG,
                    role_name: roleKey,
                    [field]: value,
                    is_new: true
                }];
            }
        });
    };

    const handleSave = async (roleKey) => {
        setSaving(true);
        const config = getConfigForRole(roleKey);
        try {
            // Ensure any missing fields from UI state (if creating new) are filled with defaults
            const payload = { ...DEFAULT_CONFIG, ...config };

            const res = await fetch('/api/admin/ai/models', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                toast.success(`Settings saved for ${roleKey}`);
                fetchData();
            } else {
                throw new Error("API responded with error");
            }
        } catch (err) {
            console.error("Failed to save", err);
            toast.error("Failed to save settings");
        }
        setSaving(false);
    };

    if (loading) return <div className="text-stone-500 p-8">Loading configuration...</div>;

    const getRequiredRole = (roleKey) => {
        if (roleKey === 'default_chat') return 'chat';
        if (roleKey === 'rag_answer') return 'rag_synthesis';
        if (roleKey === 'voice_answer') return 'voice_answer';
        if (roleKey === 'voice_synthesis') return 'voice_synthesis';
        if (roleKey === 'voice_transcription') return 'voice_transcription';
        if (roleKey === 'document_analysis') return 'intent'; // key intent/extraction
        if (roleKey === 'rag_embedding') return 'embedding';
        if (roleKey === 'seo_description') return 'chat';
        return 'chat';
    };

    return (
        <div className="h-full flex flex-col gap-6 text-stone-300 max-w-5xl mx-auto">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Cpu size={24} className="text-blue-500" />
                    Model Role Assignments
                </h3>
                <button onClick={fetchData} className="p-2 hover:bg-stone-800 rounded bg-stone-900 border border-stone-800 text-stone-400 hover:text-white">
                    <RefreshCw size={16} />
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {standardRoles.map(role => {
                    const config = getConfigForRole(role.key);
                    const isEmbedding = role.key === 'rag_embedding';
                    const requiredCapability = getRequiredRole(role.key);

                    // Filter models strict by role
                    const compatibleModels = availableModels.filter(m =>
                        m.roles && m.roles.includes(requiredCapability)
                    );

                    // Check if current is valid
                    // If availableModels is empty (loading or error), we don't warn yet. 
                    // If models loaded but none compatible, warn.
                    const hasCompatible = compatibleModels.length > 0;

                    return (
                        <div key={role.key} className="bg-stone-900 border border-stone-800 rounded-lg p-6 flex flex-col gap-4 relative overflow-hidden group hover:border-stone-700 transition-colors">
                            {/* Header */}
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-lg text-white">{role.label}</h4>
                                    <p className="text-sm text-stone-500">{role.desc}</p>
                                    <div className="text-xs text-stone-600 mt-1 uppercase font-mono tracking-wider">
                                        Required Capability: {requiredCapability}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {/* Status Dot */}
                                    {config.is_active !== false ? (
                                        <span className="flex items-center gap-1.5 text-xs font-bold text-green-400 bg-green-900/20 px-2 py-1 rounded">
                                            <Activity size={12} /> Active
                                        </span>
                                    ) : (
                                        <span className="text-xs font-bold text-red-500 bg-red-900/20 px-2 py-1 rounded">Inactive</span>
                                    )}
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="grid grid-cols-12 gap-4 items-end bg-stone-950/30 p-4 rounded-md">
                                <div className="col-span-5">
                                    <label className="block text-xs font-bold mb-1 text-stone-400">Assigned Model</label>
                                    <select
                                        className="w-full bg-stone-800 border border-stone-700 p-2 rounded text-white text-sm focus:border-blue-500 outline-none"
                                        value={config.model_name}
                                        onChange={e => handleConfigChange(role.key, 'model_name', e.target.value)}
                                        disabled={!hasCompatible && availableModels.length > 0}
                                    >
                                        {/* Show selected value even if not in list (to avoid it disappearing) */}
                                        {!compatibleModels.some(m => m.value === config.model_name) && (
                                            <option value={config.model_name}>{config.model_name} (Current - Invalid/Missing)</option>
                                        )}

                                        {availableModels.length > 0 && !hasCompatible && (
                                            <option disabled>No compatible models found for '{requiredCapability}'</option>
                                        )}

                                        {compatibleModels.map(m => {
                                            const statusLabel = m.isLoaded ? '(Loaded)' : '(Offline)';
                                            return (
                                                <option key={m.value} value={m.value}>
                                                    {m.label} {statusLabel} {m.provider && m.provider !== 'ollama' ? `[${m.provider}]` : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                {!isEmbedding && (
                                    <>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-bold mb-1 text-stone-400">Temp</label>
                                            <input
                                                type="number" step="0.1" min="0" max="2"
                                                className="w-full bg-stone-800 border border-stone-700 p-2 rounded text-white text-sm"
                                                value={config.temperature || 0.7}
                                                onChange={e => handleConfigChange(role.key, 'temperature', parseFloat(e.target.value))}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-bold mb-1 text-stone-400">Max Tokens</label>
                                            <input
                                                type="number" step="128"
                                                className="w-full bg-stone-800 border border-stone-700 p-2 rounded text-white text-sm"
                                                value={config.max_tokens || 2048}
                                                onChange={e => handleConfigChange(role.key, 'max_tokens', parseInt(e.target.value))}
                                            />
                                        </div>
                                    </>
                                )}



                                <div className="col-span-3 flex justify-end gap-2">
                                    <button
                                        onClick={() => openPromptModal(role.key, role.label, config.model_name)}
                                        className="bg-stone-800 text-stone-400 border border-stone-700 hover:bg-stone-700 hover:text-white px-3 py-2 rounded text-sm font-bold transition-all flex items-center gap-2"
                                        title="Edit System Prompt"
                                    >
                                        <FileText size={14} /> Prompt
                                    </button>
                                    <button
                                        onClick={() => openTestModal(role.key, role.label)}
                                        className="bg-purple-900/20 text-purple-400 border border-purple-800/50 hover:bg-purple-800/30 hover:text-purple-200 px-4 py-2 rounded text-sm font-bold transition-all flex items-center gap-2"
                                        title="Test this configuration directly"
                                    >
                                        <Play size={14} /> Test
                                    </button>
                                    <button
                                        onClick={() => handleSave(role.key)}
                                        disabled={saving || !hasCompatible}
                                        className="bg-blue-600/20 text-blue-400 border border-blue-600/50 hover:bg-blue-600 hover:text-white px-4 py-2 rounded text-sm font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Save size={14} /> Update
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <AiRoleTestModal
                isOpen={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                roleKey={testRole?.key}
                roleLabel={testRole?.label}
            />

            <PromptEditorModal
                isOpen={promptModalOpen}
                onClose={() => setPromptModalOpen(false)}
                roleKey={promptEditRole?.key}
                roleLabel={promptEditRole?.label}
                modelName={promptEditRole?.model}
            />
        </div >
    );
};

export default ModelSwitcher;
